<template>
	<form class="main-wrapper mt-4 mb-3" @submit.prevent="handleSubmit">
		<BannerGeneral
			:id="id"
			ref="general"
			:name="name"
			:priority="priority"
			:status="status"
			:widget-type="widgetType"
			name-label="Hero banner name*"
			@update:name="name = $event"
			@update:priority="priority = $event"
			@update:status="status = $event"
		/>

		<BannerDate
			ref="date"
			:status="status"
			:start-date="startDate"
			:start-time="startTime"
			:end-date="endDate"
			:end-time="endTime"
			@update:startDate="startDate = $event"
			@update:startTime="startTime = $event"
			@update:endDate="endDate = $event"
			@update:endTime="endTime = $event"
		/>

		<BannerContentWrapper
			ref="content"
			:required-link="!!desktopBanner && !!mobileBanner"
			:banner-link="bannerLink"
			desktop-img="/assets/images/hero-banner-desktop.svg"
			mobile-img="/assets/images/hero-banner-mobile.svg"
			@update:bannerLink="bannerLink = $event"
		>
			<template #title>
				Hero banner
			</template>
			<template #detail>
				Banner will be displayed on the top of homepage. Using 2 sizes
				of banner for desktop and mobile.
			</template>
			<BaseMediaSelectorBox
				ref="desktopBanner"
				v-model="desktopBanner"
				title="Desktop banner"
				no-image-text="No banner"
				show-helper
				width="2000"
				height="720"
				class="mt-4"
				required
			/>
			<BaseMediaSelectorBox
				ref="mobileBanner"
				v-model="mobileBanner"
				title="Mobile banner"
				no-image-text="No banner"
				show-helper
				width="720"
				height="480"
				class="mt-2"
				required
			/>
		</BannerContentWrapper>

		<BaseActionPanelStickyFooter
			:disabled-confirm="isLoading || isUpdating"
			:is-edit="isEditMode"
			:remove-text="isEditMode ? 'Remove banner' : null"
			@onConfirm="handleSubmit"
			@onCancel="$router.push({ name: 'BannerHeroList'})"
			@onRemove="isEditMode ? $refs['modal-remove'].open() : null"
		/>

		<BaseModalConfirmDelete
			ref="modal-remove"
			:handle-remove="deleteBanner.bind(null, id)"
			title="Remove this banner?"
			description="By removing this, all banner info will be disappeared from the list and linked widgets."
			@onSuccess="$router.push({ name: 'BannerHeroList'})"
		/>
	</form>
</template>

<script>
import { mapActions } from 'vuex';

import BannerGeneral from '@/components/BannerGeneral.vue';
import BannerDate from '@/components/BannerDate.vue';
import BannerContentWrapper from '@/components/BannerContentWrapper.vue';
import BaseMediaSelectorBox from '@/components/BaseMediaSelectorBox.vue';

import { BANNER_TYPE, BANNER_IMAGE_TYPE } from '../enums/banners';
import { convertDateTimeToUTC } from '../assets/js/helpers';

export default {
	name: 'BannerHeroForm',

	components: {
		BannerGeneral,
		BannerDate,
		BannerContentWrapper,
		BaseMediaSelectorBox,
	},

	props: {
		isEditMode: {
			type: Boolean,
			default: false,
		},
		isLoading: {
			type: Boolean,
			default: false,
		},
		isUpdating: {
			type: Boolean,
			default: false,
		},
		data: {
			type: Object,
			default: null,
		},
		bannerType: {
			type: String,
			default: BANNER_TYPE.hero,
		},
	},

	data() {
		return {
			id: null,
			name: '',
			priority: 0,
			status: true,
			startDate: null,
			startTime: '00:00',
			endDate: null,
			endTime: '23:59',
			desktopBanner: null,
			mobileBanner: null,
			bannerLink: '',
		};
	},

	computed: {
		widgetType() {
			const widget = {
				hero: 'hero_banner',
				highlight: 'highlight_banner',
				small: 'small_banner',
				// In case text widget type and banner type are same
				text_widget: 'text_widget',
				picture_thumbnail: 'picture_thumbnail',
			};

			return widget[this.bannerType];
		},
	},

	watch: {
		data: {
			immediate: true,
			handler(value) {
				if (this.isEditMode && !this.isUpdating) {
					this.id = value.id;
					this.name = value.name;
					this.priority = value.priority;
					this.status = value.status;
					this.startDate = value.startDate;
					this.startTime = value.startTime;
					this.endDate = value.endDate;
					this.endTime = value.endTime;
					this.desktopBanner = value.banners.desktopBanner;
					this.mobileBanner = value.banners.mobileBanner;
					this.bannerLink = value.banners.bannerLink;
				}
			},
		},
	},

	methods: {
		...mapActions({
			deleteBanner: 'banners/deleteBanner',
		}),

		handleChangeEndDate(value) {
			this.endDate = value;
		},

		addBanner(file, type, link) {
			const result = {};
			result.file = file ? file.id : null;
			result.type = type;

			if (link) {
				result.link = link;
			}

			return result;
		},

		handleSubmit() {
			const generalRef = this.$refs.general.$v;
			const dateRef = this.$refs.date.$v;

			const contentRef = this.$refs.content.$v;
			const desktopBannerRef = this.$refs.desktopBanner.$v;
			const mobileBannerRef = this.$refs.mobileBanner.$v;

			generalRef.$touch();
			dateRef.$touch();

			contentRef.$touch();
			desktopBannerRef.$touch();
			mobileBannerRef.$touch();

			if (
				!generalRef.$invalid &&
				!dateRef.$invalid &&
				!contentRef.$invalid &&
				!desktopBannerRef.$invalid &&
				!mobileBannerRef.$invalid
			) {
				const data = {
					name: this.name,
					type: BANNER_TYPE.hero,
					priority: Number(this.priority, 10),
					is_visible: this.status,
					start_at: convertDateTimeToUTC(this.startDate, this.startTime),
					end_at: convertDateTimeToUTC(this.endDate, this.endTime),
					banners: [
						this.addBanner(
							this.desktopBanner,
							BANNER_IMAGE_TYPE.homepage_desktop_banner_image,
							this.bannerLink,
						),
						this.addBanner(
							this.mobileBanner,
							BANNER_IMAGE_TYPE.homepage_mobile_banner_image,
							this.bannerLink,
						),
					],
				};

				this.$emit('onSubmit', data);
			}
		},

		handleRemove() {
			this.$emit('onRemove');
		},
	},
};
</script>

<style lang="scss" scoped>
	.remove-banners {
		font-size: rem(14);
		color: $color-orange;

		align-items: center;

		cursor: pointer;
	}
</style>
